import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Select,
  MenuItem,
  List,
  ListItem,
  Checkbox,
  Snackbar,
  FormControl,
  RadioGroup,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { genAccount, USER_FIELDS } from "../../services/user/userApi";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAppDispatch } from "../../app/hooks";
import { addUsers } from "../../features/user/userSlice";

const steps = ["Upload XLSX", "Chọn sheet", "Chọn user", "Hoàn thành"];

export default function ImportUserStep({ onImport }) {
    const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [workbook, setWorkbook] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [excelColumns, setExcelColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [success, setSuccess] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [selectRows, setSelectRows] = useState([]);
  const [selectColumns, setSelectColumns] = useState([]);
  const [checkResults, setCheckResults] = useState([]);

  // STEP 1: upload file
  const handleUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const wb = XLSX.read(data, { type: "array" });
      setWorkbook(wb);
      setActiveStep(1);
    };

    reader.readAsArrayBuffer(file);
  };

  // STEP 2: chọn sheet
  const handleSelectSheet = (name) => {
    setSheetName(name);
    const ws = workbook.Sheets[name];
    const rawData = XLSX.utils.sheet_to_json(ws, {
      header: 1, // ✅ trả về array-of-arrays
      defval: "", // ✅ giữ ô trống
    });

    const maxColumn = Math.max(...rawData.map((row) => row.length), 0);

    const excelColumns = Array.from(
      { length: maxColumn },
      (_, i) => `col_${i}`,
    );

    const rows = rawData.slice(0, 10).map((row, rowIndex) => {
      const obj = { id: rowIndex };

      excelColumns.forEach((col, colIndex) => {
        obj[col] = row[colIndex] ?? "";
      });

      return obj;
    });

    setExcelColumns(excelColumns);
    setRows(rows);
    setRawData(rawData);
    setActiveStep(2);
  };

  // STEP 3: chọn user
  const selectUsers = (index) => {
    const mappedColumns = Object.entries(columnMap)
      .filter(([_, field]) => field) // chỉ giữ cột có field
      .map(([excelCol, field]) => ({
        excelCol,
        field,
      }));

    const selectRows = rawData.map((row, rowIndex) => {
      const obj = { id: rowIndex };

      mappedColumns.forEach(({ excelCol, field }) => {
        const colIndex = Number(excelCol.replace("col_", ""));
        obj[field] = row[colIndex] ?? "";
      });

      return obj;
    });

    const selectColumns = mappedColumns.map(({ field }) => ({
      field,
      headerName: field.toUpperCase(),
      minWidth: 160,
      flex: 1,
    }));
    // selectColumns.splice(1, 0, {
    //   field: "account",
    //   headerName: "Account",
    //   width: 180,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params) => {
    //     const options = [];
    //     return (
    //     <Select
    //       size="small"
    //       fullWidth
    //       value={params.value ?? ""}
    //       onClick={(e) => e.stopPropagation()} // ✅ không trigger row click
    //       onChange={(e) => {
    //         const newValue = e.target.value;

    //         // ✅ update rows state
    //         setRows((prev) =>
    //           prev.map((row) =>
    //             row.id === params.id ? { ...row, account: newValue } : row,
    //           ),
    //         );
    //       }}
    //     >
    //       {options.map((opt) => (
    //         <MenuItem key={opt.value} value={opt.value}>
    //           {opt.label}
    //         </MenuItem>
    //       ))}
    //     </Select>
    //   )},
    // });

    setSelectRows(selectRows);
    setSelectColumns(selectColumns);
    setActiveStep(3);
  };

  // STEP 4: check selected row
  const handleCheck = async () => {
    const newResults = await Promise.all(
      selectedIds.map(async (i) => {
        const row = selectRows[i];
        const r = checkResults.find((r) => r.id === row.id);
        if (r) {
          return r;
        }
        const { result } = await genAccount(row.name);
        const newResult = { id: row.id, candidateIdx: 0 };
        if (result) {
          const newUser = {
            ...row,
            email: result.account,
            id: `tmp_${crypto.randomUUID()}`,
          };
          if (result.users.length > 0) {
            // conflict
            newResult.state = "conflict";
            newResult.candidates = [newUser, ...result.users];
            dispatch(addUsers(result.users));
          } else {
            // OK
            newResult.state = "new";
            newResult.candidates = [newUser];
          }
        } else {
          newResult.state = "error";
          newResult.candidates = [row];
        }
        return newResult;
      }),
    );

    // check
    setCheckResults(newResults);
    setActiveStep(4);
  };

  const columns = excelColumns.map((col) => ({
    field: col,
    minWidth: 160,
    flex: 1,
    sortable: false,

    renderHeader: () => (
      <Select
        size="small"
        value={columnMap[col] || ""}
        onChange={(e) =>
          setColumnMap((prev) => ({
            ...prev,
            [col]: e.target.value,
          }))
        }
      >
        <MenuItem value="">-- Không import --</MenuItem>
        {USER_FIELDS.map((f) => (
          <MenuItem key={f.key} value={f.key}>
            {f.alias}
          </MenuItem>
        ))}
      </Select>
    ),
  }));

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* STEP CONTENT */}
      {activeStep === 0 && (
        <Box>
          <Button variant="contained" component="label">
            Upload XLSX
            <input
              hidden
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUpload}
            />
          </Button>
        </Box>
      )}

      {activeStep === 1 && (
        <Box>
          <Typography>Chọn sheet</Typography>
          <Select
            fullWidth
            value={sheetName}
            onChange={(e) => handleSelectSheet(e.target.value)}
          >
            {workbook.SheetNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {activeStep === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Map cột Excel với User info
          </Typography>

          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            autoHeight // ✅ grid tự co chiều cao
            hideFooter // ✅ Ẩn toàn bộ footer (pagination)
            disableColumnMenu // ✅ Không cần menu
            density="compact"
            columnHeaderHeight={88}
          />
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button variant="outlined" onClick={() => setActiveStep(1)}>
              ◀ Back (Chọn sheet)
            </Button>
            <Button variant="contained" sx={{ mt: 2 }} onClick={selectUsers}>
              Tiếp tục
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 3 && (
        <Box>
          <Typography>Chọn user muốn import</Typography>

          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={selectRows}
              columns={selectColumns}
              checkboxSelection
              rowSelectionModel={selectedIds}
              disableRowSelectionOnClick
              density="compact"
              selecte
              onRowSelectionModelChange={(ids) => setSelectedIds(ids)}
              autoPageSize
            />
          </Box>

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button variant="outlined" onClick={() => setActiveStep(2)}>
              ◀ Back (Chọn column)
            </Button>

            <Button
              variant="contained"
              disabled={selectedIds.length === 0}
              onClick={handleCheck}
            >
              Check ({selectedIds.length})
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 4 && (
        <>
          <CheckTable
            checkResults={checkResults}
            setCheckResults={setCheckResults}
          ></CheckTable>
          {/* {checkResults.map((result, iResult) => {
            return (
              <CandidateSelect
                key={iResult}
                candidateIdx={result.selected}
                candidates={result.candidates}
                onChange={(selected) => {
                  setCheckResults((prev) =>
                    prev.toSpliced(iResult, 1, {
                      ...prev[iResult],
                      selected,
                    }),
                  );
                }}
              />
            );
          })} */}

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button variant="outlined" onClick={() => setActiveStep(3)}>
              ◀ Back (Chọn user)
            </Button>
            <Button
              variant="contained"
              disabled={selectedIds.length === 0}
              onClick={() => {
                const users = selectedIds.map((rowIdx) => {
                  const r = checkResults.find((r) => r.id === rowIdx);
                  return r.candidates[r.candidateIdx];
                });
                console.log("import users: ", users);
                onImport(users);
                //   setSuccess(true);
              }}
            >
              Import ({selectedIds.length})
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={success}
        autoHideDuration={3000}
        message="Import user thành công"
        onClose={() => setSuccess(false)}
      />
    </Box>
  );
}

function CheckTable({ checkResults, setCheckResults }) {
//   console.log(checkResults);

  function handleCandidateChange(resultId, candidateIdx) {
    setCheckResults((prev) =>
      prev.map((r) => (r.id === resultId ? { ...r, candidateIdx } : r)),
    );
  }

  const renderStateIcon = (state) => {
    // console.log("state: ", state);
    switch (state) {
      case "new":
        return <CheckCircleIcon color="success" fontSize="small" />;
      case "conflict":
        return <WarningAmberIcon color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  // transform
  const rows = checkResults.map((r) => {
    const user = r.candidates[r.candidateIdx];
    const options = r.candidates.map((u, idx) => ({
      value: idx,
      label: u.email,
    }));
    return {
      state: r.state,
      candidateIdx: r.candidateIdx,
      ...user,
      options,
      resultId: r.id,
    };
  });
  const columns = [
    {
      field: "State",
      headerName: "",
      width: 40,
      sortable: false,
      renderCell: (params) => renderStateIcon(params.row.state),
    },
    { field: "name", headerName: "Name", flex: 1 },
    // các column khác
    {
      field: "account",
      headerName: "Account",
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        // console.log(params);
        const row = params.row;
        const options = row.options;
        return (
          <Select
            size="small"
            fullWidth
            value={row.candidateIdx}
            onClick={(e) => e.stopPropagation()} // ✅ không trigger row click
            onChange={(e) => {
              // ✅ update candidateIdx
              const candidateIdx = Number(e.target.value);
              handleCandidateChange(row.resultId, candidateIdx);
            }}
          >
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
  ];

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowClassName={(params) =>
        params.row.candidates?.length > 1 ? "row-warning" : ""
      }
    />
  );
}

function CandidateSelect({ candidateIdx, candidates, onChange }) {
  return (
    <FormControl fullWidth>
      <RadioGroup
        value={candidateIdx}
        onChange={(_e, v) => {
          const idx = Number(v);
          onChange(idx);
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48} />
              <TableCell>Email</TableCell>
              <TableCell>Birth</TableCell>
              <TableCell>Thành phần</TableCell>
              <TableCell>Pháp danh</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {candidates.map((u, i) => (
              <TableRow
                key={i}
                hover
                selected={candidateIdx === i}
                onClick={() => onChange(i)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell padding="checkbox">
                  <Radio value={i} />
                </TableCell>

                <TableCell>{u.email}</TableCell>
                <TableCell>{u.birth || "-"}</TableCell>
                <TableCell>{u.thanhPhan || "-"}</TableCell>
                <TableCell>{u.phapDanh || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </RadioGroup>
    </FormControl>
  );
}
